<script setup lang="ts">
import { Translation } from 'vue-i18n';
import { computed } from 'vue';

import { i18n } from '@apps/main/modules/localization/localizationSetup';

import type { AppButtonState } from '@libs/ui';
import { AppButton } from '@libs/ui';
import type { Money } from '@libs/trip-planner-interfaces';
import { formatWithCurrency } from '@libs/utils';

import type { SelectedActivityTicketsInfo } from '../models/activityDetailsSummary';

const { t } = i18n.global;
interface ActivityDetailsPriceSummaryMobileProps {
  selectedTickets?: SelectedActivityTicketsInfo;
  fromCheapestPrice?: Money;
}

const props = defineProps<ActivityDetailsPriceSummaryMobileProps>();

interface ActivityDetailsPriceSummaryMobileEmits {
  (event: 'open-tickets-selector'): void;
}
const emit = defineEmits<ActivityDetailsPriceSummaryMobileEmits>();

const areTicketsSelected = computed(
  () =>
    props.selectedTickets?.selectedTicketTypes.length &&
    props.selectedTickets.totalPrice
);

const formattedFromPrice = computed(() => {
  if (!props.fromCheapestPrice) {
    return;
  }
  return formatWithCurrency(
    props.fromCheapestPrice.amount,
    props.fromCheapestPrice.currency
  );
});

const formattedTotalPrice = computed(() => {
  if (!props.selectedTickets?.totalPrice) {
    return;
  }
  return formatWithCurrency(
    props.selectedTickets.totalPrice.amount,
    props.selectedTickets.totalPrice.currency
  );
});

const totalAmountTickets = computed(
  () =>
    props.selectedTickets?.selectedTicketTypes.reduce(
      (totalAmountTickets, currentTicket) =>
        (totalAmountTickets += currentTicket.amount),
      0
    ) ?? 0
);

const appButtonState = computed<AppButtonState>(() => {
  if (!props.fromCheapestPrice && !props.selectedTickets) {
    return 'loadingSpinner';
  }
  return !props.fromCheapestPrice ? 'disabled' : 'enabled';
});

const appButtonText = computed<string>(() =>
  !props.fromCheapestPrice
    ? t('planner.no_dates_available')
    : areTicketsSelected.value
    ? t('planner.change')
    : t('planner.show_dates')
);
</script>

<template>
  <div class="activity-details-price-summary-mobile">
    <div v-if="props.fromCheapestPrice && !areTicketsSelected">
      <Translation
        keypath="planner.from_price_per_person"
        tag="span"
        class="from-price"
      >
        <template #price>
          <br />
          <span class="price">{{ formattedFromPrice }}</span>
        </template>
      </Translation>
    </div>
    <div
      v-else-if="areTicketsSelected && props.selectedTickets"
      class="selected-tickets"
    >
      <span class="selected-tickets-amount">
        {{
          `${totalAmountTickets} ${$t(
            'planner.tickets_selected',
            totalAmountTickets
          )}`
        }}
      </span>
      <span class="price">{{ formattedTotalPrice }}</span>
    </div>
    <AppButton
      type="important"
      :state="appButtonState"
      size="medium"
      :wide="!props.fromCheapestPrice"
      :ariaLabel="$t('a11y.open_tickets_selector')"
      @click="emit('open-tickets-selector')"
    >
      {{ appButtonText }}
    </AppButton>
  </div>
</template>

<style scoped lang="scss">
.activity-details-price-summary-mobile {
  display: flex;
  justify-content: space-between;
  border-radius: var(--border-radius-12);
  background-color: var(--color-white);
  box-shadow: var(--shadow-elevation-100);
  padding: var(--spacing-12);
  align-items: center;
  gap: var(--spacing-8);

  .from-price {
    @include font-body-s-light;
    .price {
      @include font-body-l-bold;
    }
  }
  .selected-tickets {
    display: flex;
    flex-direction: column;
    .selected-tickets-amount {
      @include font-body-s-light;
    }
    .price {
      @include font-body-l-bold;
    }
  }
}
</style>
